// src/features/hotels/hooks/useHotelValidation.ts

import { useCallback } from 'react';
import { useTranslate, useDataProvider } from 'react-admin';
import { HotelFormData, HotelValidationErrors } from '../types';

/**
 * Hook de validación para hoteles
 * Sigue el mismo patrón que useGroupValidation
 */
export const useHotelValidation = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();

  const validateHotelName = useCallback(async (
    value?: string,
    hotelId?: string
  ): Promise<string | undefined> => {
    if (!value || value.trim().length === 0) {
      return translate('resources.hotels.validation.name_required', 'El nombre del hotel es obligatorio');
    }

    if (value.trim().length < 3) {
      return translate('resources.hotels.validation.name_min_length', 'El nombre debe tener al menos 3 caracteres');
    }

    if (value.trim().length > 100) {
      return translate('resources.hotels.validation.name_max_length', 'El nombre no puede exceder 100 caracteres');
    }

    // Validar unicidad del nombre
    try {
      const response = await dataProvider.getList('hotels', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: { name: value.trim() },
      });

      // Si encontramos un hotel con el mismo nombre y no es el actual
      if (response.data.length > 0 && response.data[0].id !== hotelId) {
        return translate('resources.hotels.validation.name_exists', 'Ya existe un hotel con este nombre');
      }
    } catch (error) {
      console.warn('Error checking hotel name uniqueness:', error);
      // No bloquear la validación si hay error de red
    }

    return undefined;
  }, [translate, dataProvider]);

  /**
   * Valida la dirección del hotel
   */
  const validateAddress = useCallback((value?: string) => {
    if (!value) return undefined;

    if (value.trim().length > 255) {
      return translate('resources.hotels.validation.address_max_length', 'La dirección no puede exceder 255 caracteres');
    }

    return undefined;
  }, [translate]);

  /**
   * Valida el código de instalación
   */
  const validateInstallationCode = useCallback(async (
    value?: string,
    hotelId?: string
  ): Promise<string | undefined> => {
    if (!value) return undefined;

    // Validar que sea un string válido (al menos 1 carácter)
    if (value.trim().length === 0) {
      return translate('resources.hotels.validation.installation_code_required', 
        'El código de instalación no puede estar vacío');
    }

    // Validar longitud máxima razonable
    if (value.trim().length > 50) {
      return translate('resources.hotels.validation.installation_code_max_length', 
        'El código de instalación no puede exceder 50 caracteres');
    }

    // Validar unicidad del código
    try {
      const response = await dataProvider.getList('hotels', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: { installationCode: value },
      });

      if (response.data.length > 0 && response.data[0].id !== hotelId) {
        return translate('resources.hotels.validation.installation_code_exists', 
          'Ya existe un hotel con este código de instalación');
      }
    } catch (error) {
      console.warn('Error checking installation code uniqueness:', error);
    }

    return undefined;
  }, [translate, dataProvider]);

  /**
   * Valida el idioma por defecto
   */
  const validateDefaultLanguage = useCallback((value?: string) => {
    if (!value) return undefined;

    const validLanguages = ['ES', 'EN', 'FR', 'DE', 'IT', 'PT'];
    if (!validLanguages.includes(value)) {
      return translate('resources.hotels.validation.invalid_language', 'Idioma no válido');
    }

    return undefined;
  }, [translate]);

  /**
   * Valida el grupo asignado
   */
  const validateGroup = useCallback((value?: string) => {
    const userRole = localStorage.getItem('role');

    // Solo SuperAdmin puede crear hoteles sin grupo
    if (!value && userRole !== 'SuperAdmin') {
      return translate('resources.hotels.validation.group_required', 'Debe seleccionar un grupo');
    }

    return undefined;
  }, [translate]);

  /**
   * Valida el email de contacto
   */
  const validateEmail = useCallback((value?: string) => {
    if (!value) return undefined;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return translate('resources.hotels.validation.email_format', 'El formato del email no es válido');
    }

    return undefined;
  }, [translate]);

  /**
   * Valida el teléfono de contacto
   */
  const validatePhone = useCallback((value?: string) => {
    if (!value) return undefined;

    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{7,15}$/;
    if (!phoneRegex.test(value)) {
      return translate('resources.hotels.validation.phone_format', 'El formato del teléfono no es válido');
    }

    return undefined;
  }, [translate]);

  /**
   * Validación completa del formulario de hotel
   */
  const validateHotelForm = useCallback(async (
    formData: HotelFormData,
    hotelId?: string
  ): Promise<HotelValidationErrors> => {
    const errors: HotelValidationErrors = {};

    // Validar nombre
    const nameError = await validateHotelName(formData.name, hotelId);
    if (nameError) errors.name = nameError;

    // Validar dirección
    const addressError = validateAddress(formData.address);
    if (addressError) errors.address = addressError;

    // Validar código de instalación
    const codeError = await validateInstallationCode(formData.installationCode, hotelId);
    if (codeError) errors.installationCode = codeError;

    // Validar idioma por defecto
    const languageError = validateDefaultLanguage(formData.defaultLanguage);
    if (languageError) errors.defaultLanguage = languageError;

    // Validar grupo
    const groupError = validateGroup(formData.groupId);
    if (groupError) errors.groupId = groupError;

    // Validar email si está presente
    const emailError = validateEmail(formData.contactEmail);
    if (emailError) errors.contactEmail = emailError;

    // Validar teléfono si está presente
    const phoneError = validatePhone(formData.contactPhone);
    if (phoneError) errors.contactPhone = phoneError;

    return errors;

    
  }, [
    validateHotelName,
    validateAddress, 
    validateInstallationCode,
    validateDefaultLanguage,
    validateGroup,
    validateEmail,
    validatePhone
  ]);


  /**
 * Valida la duración de asignación por email
 */
const validateEmailAssignmentDuration = useCallback((
  value?: number,
  operationMode?: number
): string | undefined => {
  if (operationMode === 2 && !value) {
    return translate('resources.hotels.validation.email_duration_required', 
      'La duración de asignación es requerida para modo Email Based');
  }
  
  if (value && (value < 1 || value > 168)) {
    return translate('resources.hotels.validation.email_duration_range', 
      'La duración debe estar entre 1 y 168 horas (1 semana)');
  }
  
  return undefined;
}, [translate]);

/**
 * Valida el tiempo de expiración del código
 */
const validateCodeExpirationMinutes = useCallback((
  value?: number,
  operationMode?: number
): string | undefined => {
  if (operationMode === 2 && !value) {
    return translate('resources.hotels.validation.code_expiration_required', 
      'El tiempo de expiración es requerido para modo Email Based');
  }
  
  if (value && (value < 5 || value > 1440)) {
    return translate('resources.hotels.validation.code_expiration_range', 
      'El tiempo de expiración debe estar entre 5 y 1440 minutos (24 horas)');
  }
  
  return undefined;
}, [translate]);

/**
 * Valida el tiempo de reserva de taquilla
 */
const validateLockerHoldMinutes = useCallback((
  value?: number
): string | undefined => {
  if (value && (value < 5 || value > 120)) {
    return translate('resources.hotels.validation.locker_hold_range', 
      'El tiempo de reserva debe estar entre 5 y 120 minutos');
  }
  
  return undefined;
}, [translate]);

/**
 * Valida los tag types seleccionados
 */
const validateSupportedTagTypes = useCallback((
  value?: string[],
  operationMode?: number
): string | undefined => {
  if (operationMode === 1 && (!value || value.length === 0)) {
    return translate('resources.hotels.validation.tag_types_required', 
      'Debe seleccionar al menos un tipo de tag para el modo Tag Only');
  }
  
  if (value && value.length > 10) {
    return translate('resources.hotels.validation.tag_types_max', 
      'No puede seleccionar más de 10 tipos de tag');
  }
  
  return undefined;
}, [translate]);

/**
 * Valida el modo de operación
 */
const validateOperationMode = useCallback((
  value?: number
): string | undefined => {
  if (!value || (value !== 1 && value !== 2)) {
    return translate('resources.hotels.validation.operation_mode_invalid', 
      'Modo de operación inválido');
  }
  
  return undefined;
}, [translate]);


  return {
    validateHotelName,
    validateAddress,
    validateInstallationCode,
    validateDefaultLanguage,
    validateGroup,
    validateEmail,
    validatePhone,
    validateHotelForm,
    validateEmailAssignmentDuration,
    validateCodeExpirationMinutes,
    validateLockerHoldMinutes,
    validateSupportedTagTypes,
    validateOperationMode,
  };
};